import { ComponentProps } from 'react';
import { useNavigate } from 'react-router-dom';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { Button } from 'app/agriculture/components/Button/Button';
import { InfoIcon, LinkIcon } from 'app/agriculture/icons';
import { routeConfig } from 'app/agriculture/router/routeConfig';
import { useDevice } from 'app/agriculture/queries/devices/useDevice';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { useFarm } from 'app/agriculture/hooks/useFarm/useFarm';
import { useContext } from 'react';
import { ServicesContext } from 'app/items/shared/context_interface';
import { useFarmPath } from 'app/agriculture/router/useFarmPath';

interface DeviceActionsProps {
    deviceId: string;
    sticky?: boolean;
}

export function DeviceActions({ deviceId, sticky = false }: DeviceActionsProps) {
    const navigate = useNavigate();

    const deviceTypeSpecificActions = useDeviceTypeSpecificActions({ deviceId });
    const brandSpecificActions = useBrandSpecificActions({ deviceId });

    return (
        <Actions
            sticky={sticky}
            primaryAction={{
                text: 'Edit',
                startIcon: 'edit',
                onClick: () => {
                    navigate('edit');
                },
            }}
            secondaryActions={[...brandSpecificActions, ...deviceTypeSpecificActions]}
            backAction={{
                text: 'Back',
                onClick: () => {
                    navigate(-1);
                },
            }}
        />
    );
}

// This is where all the special cased device brand specific actions should live
function useBrandSpecificActions({
    deviceId,
}: {
    deviceId: string;
}): ComponentProps<typeof Button>[] {
    const device = useDevice({ deviceId });
    const navigate = useNavigate();
    const farmPath = useFarmPath();

    switch (device.brand) {
        case 'Yarn':
            return [
                {
                    startIcon: <InfoIcon />,
                    text: 'Device Info',
                    onClick: () => {
                        navigate(
                            farmPath(`/${routeConfig.Device.rootPath}/${device.id}/more-details`),
                        );
                    },
                },
            ];
        default:
            return [];
    }
}

// This is where all the special cased device type specific actions should live
function useDeviceTypeSpecificActions({
    deviceId,
}: {
    deviceId: string;
}): ComponentProps<typeof Button>[] {
    const device = useDevice({ deviceId });
    const navigate = useNavigate();
    const farmPath = useFarmPath();
    const { farm, updateFarm } = useFarm();
    const isDefaultWeatherStation = farm.weatherStation === device.id;
    const { agricultureToastService } = useContext(ServicesContext);

    switch (device.deviceType) {
        case 'Level Monitor':
            return [
                device.infrastructureId == null
                    ? {
                          startIcon: <LinkIcon />,
                          text: 'Link Tank',
                          onClick: () => {
                              navigate(
                                  farmPath(`/${routeConfig.Device.rootPath}/${device.id}/link`),
                              );
                          },
                      }
                    : {
                          startIcon: <LinkIcon />,
                          text: 'Unlink Tank',
                          onClick: () => {
                              navigate(
                                  farmPath(`/${routeConfig.Device.rootPath}/${device.id}/unlink`),
                              );
                          },
                      },
            ];
        case 'Weather Station':
            return [
                {
                    text: 'Set as default',
                    onClick: () => {
                        updateFarm.mutate(
                            { weatherStation: device.id },
                            {
                                onSuccess: () => {
                                    agricultureToastService
                                        .showToast({
                                            title: `${device.name} is now the new default weather station`,
                                            type: AgricultureToastType.SUCCESS,
                                        })
                                        .catch((e) => {
                                            console.error(e);
                                        });
                                },
                            },
                        );
                    },
                    disabled: isDefaultWeatherStation,
                },
            ];
        default:
            return [];
    }
}
